import { Box } from "@mui/material";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as constants from "../../../constants";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import CustomTextField from "../../../shared/CustomTextFeild";
import ReusableTable, { Column } from "../../../shared/ReusableTable/ReusableTable";
import { resetImportDataDetails } from "../../../store/Financials/importDataSlice";
import { resetSubChartDetails } from "../../../store/Financials/saveChartSlice";
import { RootState } from "../../../store/store";
import { resetTableState, setPagination, setTableFilters } from "../../../store/tableSlice";
import basestyle from "../../CSS/Base.module.css";
import { getAdminBudgetInfoList, getAdminBudgetSearchCount, getBudgetInfoList, getBudgetSearchCount } from "../services/Financials/budget.service";
import { SectionTitle, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer } from "../StyledComponents/Container";
import { formatDate } from "../Utils/DataFormat";
import { formatCurrency } from "../Utils/formatUtils";
import { setActiveIndex, setOpenMenuIndex } from "../../../store/MenutItems/menuUiSlice";

const BudgetListing = () => {
    const retain = useSelector((state: RootState) => state.table.budget);
    const [budgetYear, setBudgetYear] = useState(retain?.filters?.budgetYear ?? "");
    const [page, setPage] = useState(retain?.page || 0);
    const [rowsPerPage, setRowsPerPage] = useState(retain?.pageSize || 5);
    const [count, setCount] = useState(0);
    const [budgetList, setBudgetList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userDetails = useSelector((state: RootState) => state.auth);
    const AdminProfile = useSelector((state: RootState) => state.participantAdminProfile);
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const participantDetail = useSelector((state: RootState) => state.participantDetails);
    const [participantType, setParticipantType] = useState("");

    const adminColumns: Column[] = [
        { id: "financialYear", label: translate('budgetYear'), minWidth: 150, align: "center", isHyperlink: false },
        { id: "totalParticipants", label: translate('totalParticipants'), minWidth: 150, align: "right", isHyperlink: false },
        { id: "noOfParticipantsSubmitted", label: translate('noOfParticipantsSubmitted'), minWidth: 150, align: "right", isHyperlink: false },
        { id: "noOfParticipantsInProgress", label: translate('noOfParticipantsInProgress'), minWidth: 200, align: "right", isHyperlink: false },
        { id: "noOfParticipantsNotStarted", label: translate('noOfParticipantsNotStarted'), minWidth: 200, align: "right", isHyperlink: false },
    ];

    const nonAdminColumns: Column[] = [
        { id: "forecastYear", label: translate('budgetYear'), minWidth: 150, align: "center", isHyperlink: true },
        { id: "income", label: translate('totalIncome'), minWidth: 150, align: "right", isHyperlink: false },
        { id: "expenses", label: translate('totalExpenses'), minWidth: 150, align: "right", isHyperlink: false },
        { id: "completionDate", label: translate('currentBudgetCompletionDate'), minWidth: 200, align: "center", isHyperlink: false },
        { id: "budgetReport", label: translate('budgetReport'), minWidth: 200, align: "center", isHyperlink: true },
    ];
    const handleKeyPress = (event: any) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleSearch = () => {
        setPage(0);
        if (AdminProfile.participantAdminProfile === constants.ADMIN_VIEW) {
            getAdminBudgetInfo(budgetYear, 0, rowsPerPage);
        } else {
            getBudgetInfo(budgetYear, 0, rowsPerPage);
        }
    };

    const handleClear = () => {
        setBudgetYear("");
        setPage(0);
        setRowsPerPage(5);
        if (AdminProfile.participantAdminProfile === constants.ADMIN_VIEW) {
            getAdminBudgetInfo("", 0, 5);
        } else {
            getBudgetInfo("", 0, 5);
        }
        dispatch(resetTableState({ tableKey: 'budget' }));
    };

    const handleImportBudget = () => {
        dispatch(resetImportDataDetails());
        navigate("/budgetImport", {
            state: {
                listingPageType: "budgetListing",
            },
        });
    };

    const handleNewBudget = () => {
        navigate("/newBudget");
    };

    useEffect(() => {
        if (userDetails?.defaultParticipantId) {
            const participant = (participantDetail.participantsArray as { participantId: string; participantType: string }[]).find((item) => item.participantId === userDetails.defaultParticipantId);
            setParticipantType(participant?.participantType ?? "")
        }
    }, [userDetails?.defaultParticipantId]);

    useEffect(() => {
        if (AdminProfile.participantAdminProfile === constants.ADMIN_VIEW) {
            getAdminBudgetInfo(budgetYear, page, rowsPerPage);
        } else {
            getBudgetInfo(budgetYear, page, rowsPerPage);
        }
    }, [page, rowsPerPage, AdminProfile]);

    async function getAdminBudgetInfo(budgetYear: string, pages: number, rowsPerPage: number) {
        setIsLoading(true);
        try {
            const budgetSearchVO = {
                financialYear: budgetYear,
                pageNo: -1,
                pageSize: -1
            };

            const responseCount = await getAdminBudgetSearchCount(budgetSearchVO);
            if (responseCount?.status === constants.STATUS_CODES.SUCCESS) {
                const count = responseCount?.data?.data;
                if (count > 0) {
                    setCount(count);
                    const updatedBudgetSearchVO = {
                        financialYear: budgetYear,
                        pageNo: pages + 1,
                        pageSize: rowsPerPage
                    };

                    dispatch(setTableFilters({ tableKey: 'budget', filters: { budgetYear } }));
                    dispatch(setPagination({ tableKey: 'budget', page: page, pageSize: rowsPerPage }));

                    const responseList = await getAdminBudgetInfoList(updatedBudgetSearchVO);
                    if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                        const participantForecastStatusSearchResponseVOList = responseList?.data?.data?.participantForecastStatusSearchResponseVOList;
                        if (participantForecastStatusSearchResponseVOList?.length > 0) {
                            setBudgetList(participantForecastStatusSearchResponseVOList);
                        } else {
                            setBudgetList([]);
                        }
                    } else {
                        setBudgetList([]);
                    }
                } else {
                    setBudgetList([]);
                    setCount(0);
                }
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data?.message ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {
            setIsLoading(false);
        }
    }

    async function getBudgetInfo(budgetYear: string, pages: number, rowsPerPage: number) {
        setIsLoading(true);
        try {
            const budgetSearchVO = {
                participantId: userDetails.defaultParticipantId,
                forecastYear: budgetYear,
                pageNo: -1,
                pageSize: -1
            };

            const responseCount = await getBudgetSearchCount(budgetSearchVO);
            if (responseCount?.status === constants.STATUS_CODES.SUCCESS) {
                const count = responseCount?.data?.data;
                if (count > 0) {
                    setCount(count);
                    const updatedBudgetSearchVO = {
                        participantId: userDetails.defaultParticipantId,
                        forecastYear: budgetYear,
                        pageNo: pages + 1,
                        pageSize: rowsPerPage
                    };

                    dispatch(setTableFilters({ tableKey: 'budget', filters: { budgetYear } }));
                    dispatch(setPagination({ tableKey: 'budget', page: page, pageSize: rowsPerPage }));

                    const responseList = await getBudgetInfoList(updatedBudgetSearchVO);
                    if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                        const forecastSearchVOList = responseList?.data?.data?.forecastSearchVOList;
                        if (forecastSearchVOList?.length > 0) {
                            setBudgetList(forecastSearchVOList);
                        } else {
                            setBudgetList([]);
                        }
                    } else {
                        setBudgetList([]);
                    }
                } else {
                    setBudgetList([]);
                    setCount(0);
                }
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data?.message ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {
            setIsLoading(false);
        }
    }

    const transformedBudgetList = budgetList.map((item: any) => ({
        ...item,
        expenses: item.expenses ? formatCurrency(item.expenses) : "$0.00",
        income: item.income ? formatCurrency(item.income) : "$0.00",
        completionDate: item.completionDate ? formatDate(item.completionDate) : "-",
    }));

    const handleBudgetYearClick = (rowData: any) => {
        navigate("/budgetDetails", {
            state: {
                forecastYear: rowData.forecastYear,
            },
        });
        dispatch(resetSubChartDetails());
    };

    const handleBack = () => {
        dispatch(setActiveIndex(0));
        dispatch(setOpenMenuIndex(null));
        if (AdminProfile.participantAdminProfile === constants.ADMIN_VIEW) {
            navigate("/rcboSummary");
        } else {
            navigate("/summary");
        }
    };

    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>{translate('budget')} </SectionTitle>
                    <Box sx={{ width: "100%", px: 1.5 }}>
                        <div className="search-fields-container">
                            <div className="left-fields">
                                <CustomTextField
                                    label={translate('budgetYear')}
                                    onKeyDown={handleKeyPress}
                                    value={budgetYear}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\s/g, '');
                                        setBudgetYear(value);
                                    }}
                                    className="small-text-field"
                                    size="small"
                                    inputProps={{ maxLength: 10 }}
                                />
                                <ContainedButton onClick={handleSearch}>{translate('search')}</ContainedButton>
                                <OutlinedButton onClick={handleClear}>{translate('clear')}</OutlinedButton>
                            </div>
                            <div className="right-fields">
                                {AdminProfile.participantAdminProfile === constants.ADMIN_VIEW ?
                                    <ContainedButton onClick={handleNewBudget}>{translate('newBudget')}</ContainedButton>
                                    : <ContainedButton onClick={handleImportBudget}>  {translate('importBudget')}  </ContainedButton>}
                            </div>
                        </div>
                        <ReusableTable
                            tableKey="budgetListing"
                            columns={AdminProfile.participantAdminProfile === constants.ADMIN_VIEW ? adminColumns : nonAdminColumns}
                            rows={transformedBudgetList}
                            count={count}
                            participantType={participantType}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={(newPage) => setPage(newPage)}
                            onRowsPerPageChange={(newRowsPerPage) => setRowsPerPage(newRowsPerPage)}
                            loading={isLoading}
                            onRowClick={handleBudgetYearClick}
                        />
                        <Box className={basestyle.buttoncontainer}>
                            <ContainedButton onClick={handleBack}>   {translate('back')}  </ContainedButton>
                        </Box>
                    </Box>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>
    );
};

export default BudgetListing;